import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border-2 border-[#F2EFEB] h-11 px-3 focus:outline-none focus:border-[#DFFF00] text-sm";

export default function BroadcastComposer() {
  const [audiences, setAudiences] = useState([]);
  const [audienceId, setAudienceId] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [broadcasts, setBroadcasts] = useState([]);

  const loadBroadcasts = useCallback(() => {
    api.get("/admin/broadcasts").then(({ data }) => setBroadcasts(data.broadcasts)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/broadcast/audiences").then(({ data }) => setAudiences(data.audiences)).catch(() => {});
    loadBroadcasts();
  }, [loadBroadcasts]);

  const selected = audiences.find((a) => a.id === audienceId);
  const recipientCount = selected?.count ?? 0;
  const canSend = subject.trim() && message.trim();
  const scheduleValid = !schedule || (scheduledAt && new Date(scheduledAt) > new Date());

  const payload = () => ({
    subject: subject.trim(),
    message,
    cta_label: ctaLabel.trim() || null,
    cta_url: ctaUrl.trim() || null,
    origin_url: window.location.origin,
  });

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error("Enter an email to send the test to.");
    if (!canSend) return toast.error("Add a subject and message first.");
    setTesting(true);
    try {
      await api.post("/admin/broadcast/test", { ...payload(), test_email: testEmail.trim() });
      toast.success(`Test sent to ${testEmail.trim()}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const body = {
        ...payload(),
        audience: audienceId,
        scheduled_at: schedule && scheduledAt ? new Date(scheduledAt).toISOString() : null,
      };
      const { data } = await api.post("/admin/broadcast", body);
      if (data.status === "scheduled") {
        toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()} · ${data.recipients} recipients`);
      } else {
        toast.success(`Sending to ${data.recipients} subscriber${data.recipients === 1 ? "" : "s"}...`);
      }
      setConfirming(false);
      setSubject(""); setMessage(""); setCtaLabel(""); setCtaUrl(""); setSchedule(false); setScheduledAt("");
      setTimeout(loadBroadcasts, 1500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await api.delete(`/admin/broadcasts/${id}`);
      toast.success("Scheduled broadcast cancelled");
      loadBroadcasts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="broadcast-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Composer */}
      <div className="border-2 border-[#F2EFEB]/40 p-6">
        <p className="overline text-[#DFFF00] mb-4">Compose broadcast</p>

        <label className="overline block mb-1">Audience</label>
        <select className={inputCls} value={audienceId} onChange={(e) => setAudienceId(e.target.value)} data-testid="broadcast-audience-select">
          {audiences.map((a) => (
            <option key={a.id} value={a.id} className="bg-[#090909]">{a.label} ({a.count})</option>
          ))}
        </select>

        <label className="overline block mb-1 mt-4">Subject</label>
        <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject-input" placeholder="Drop 01 is live" />

        <label className="overline block mb-1 mt-4">Message</label>
        <textarea
          className="w-full bg-transparent border-2 border-[#F2EFEB] p-3 focus:outline-none focus:border-[#DFFF00] text-sm min-h-[160px]"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="broadcast-message-input"
          placeholder={"Write your announcement.\n\nBlank lines start a new paragraph."}
        />

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="overline block mb-1">Button label (optional)</label>
            <input className={inputCls} value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} data-testid="broadcast-cta-label-input" placeholder="Shop the drop" />
          </div>
          <div>
            <label className="overline block mb-1">Button link (https)</label>
            <input className={inputCls} value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} data-testid="broadcast-cta-url-input" placeholder="https://goodgriefworld.com" />
          </div>
        </div>

        {/* Test send */}
        <div className="border-2 border-[#F2EFEB]/30 p-3 mt-6">
          <p className="overline mb-2">Send a test first</p>
          <div className="flex gap-2">
            <input className={inputCls} type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="broadcast-test-email-input" placeholder="you@yourdomain.com" />
            <button onClick={sendTest} disabled={testing} data-testid="broadcast-test-btn" className="h-11 px-4 shrink-0 border-2 border-[#F2EFEB] uppercase text-xs tracking-widest hover:bg-[#DFFF00] hover:text-[#090909] transition-colors disabled:opacity-50">
              {testing ? "..." : "Send test"}
            </button>
          </div>
        </div>

        {/* Schedule */}
        <div className="border-2 border-[#F2EFEB]/30 p-3 mt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} data-testid="broadcast-schedule-toggle" />
            Schedule for later
          </label>
          {schedule && (
            <div className="mt-3">
              <label className="overline block mb-1">Send at (your local time)</label>
              <input type="datetime-local" className={inputCls} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="broadcast-schedule-input" />
              {!scheduleValid && <p className="font-mono-ui text-xs text-[#FF2A00] mt-1">Pick a time in the future.</p>}
            </div>
          )}
        </div>

        {/* Send / confirm */}
        {!confirming ? (
          <button
            onClick={() => {
              if (!canSend) return toast.error("Add a subject and message first.");
              if (!scheduleValid) return toast.error("Pick a future date/time to schedule.");
              setConfirming(true);
            }}
            data-testid="broadcast-send-btn"
            className="mt-6 w-full h-12 border-2 border-[#DFFF00] bg-[#DFFF00] text-[#090909] uppercase tracking-widest text-sm hover:bg-transparent hover:text-[#DFFF00] transition-colors"
          >
            {schedule && scheduledAt
              ? `Schedule for ${new Date(scheduledAt).toLocaleString()} →`
              : `Send to ${recipientCount} subscriber${recipientCount === 1 ? "" : "s"} →`}
          </button>
        ) : (
          <div className="mt-6 border-2 border-[#FF2A00] p-4" data-testid="broadcast-confirm">
            <p className="text-sm mb-3">
              {schedule && scheduledAt
                ? <>This will email <strong className="text-[#DFFF00]">{recipientCount}</strong> {selected?.label} at <strong className="text-[#DFFF00]">{new Date(scheduledAt).toLocaleString()}</strong>. Schedule it?</>
                : <>This emails <strong className="text-[#DFFF00]">{recipientCount}</strong> {selected?.label} now. This can’t be undone. Send it?</>}
            </p>
            <div className="flex gap-3">
              <button onClick={doSend} disabled={sending} data-testid="broadcast-confirm-btn" className="flex-1 h-11 border-2 border-[#FF2A00] bg-[#FF2A00] text-[#090909] uppercase text-xs tracking-widest hover:bg-transparent hover:text-[#FF2A00] transition-colors disabled:opacity-60">
                {sending ? "Working..." : schedule && scheduledAt ? "Yes, schedule it" : `Yes, send to ${recipientCount}`}
              </button>
              <button onClick={() => setConfirming(false)} className="h-11 px-5 border-2 border-[#F2EFEB] uppercase text-xs tracking-widest hover:bg-[#F2EFEB] hover:text-[#090909] transition-colors">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div data-testid="broadcast-history">
        <p className="overline text-[#DFFF00] mb-4">Recent broadcasts</p>
        {broadcasts.length === 0 ? (
          <p className="opacity-60 py-8 font-mono-ui text-sm">No broadcasts sent yet.</p>
        ) : (
          <div className="space-y-2">
            {broadcasts.map((b) => (
              <div key={b.id} className="border-2 border-[#F2EFEB]/40 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm uppercase truncate">{b.subject}</p>
                  <span className={`font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 shrink-0 ${
                    b.status === "sent" ? "bg-[#DFFF00] text-[#090909]"
                      : b.status === "scheduled" ? "bg-[#F2EFEB] text-[#090909]"
                      : b.status === "cancelled" ? "bg-[#FF2A00] text-[#090909]"
                      : "bg-[#F2EFEB]/20"}`}>{b.status}</span>
                </div>
                {b.status === "scheduled" ? (
                  <div className="flex items-center justify-between gap-3 mt-1">
                    <p className="font-mono-ui text-xs opacity-60">{b.recipients} recipients · sends {new Date(b.scheduled_at).toLocaleString()}</p>
                    <button onClick={() => cancelScheduled(b.id)} data-testid={`broadcast-cancel-${b.id}`} className="font-mono-ui text-[0.65rem] uppercase text-[#FF2A00] underline underline-offset-2 shrink-0">Cancel</button>
                  </div>
                ) : (
                  <p className="font-mono-ui text-xs opacity-60 mt-1">
                    {b.recipients} recipients · {b.sent} sent{b.failed ? ` · ${b.failed} failed` : ""} · {(b.created_at || "").slice(0, 10)}
                  </p>
                )}
              </div>
            ))}
            <button onClick={loadBroadcasts} className="font-mono-ui text-xs uppercase underline underline-offset-2 opacity-60 hover:opacity-100 mt-2">Refresh</button>
          </div>
        )}
      </div>
    </div>
  );
}
